import React, { useState } from 'react';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { FaqVisualAnswer } from './FaqVisualAnswer';
import type { FaqItem } from '../../types/faq';
import { resolveIcon } from '../../lib/iconMap';
import { useNavigate } from 'react-router-dom';
import { ResultCard, SectionHeader, StatCard } from '../common/Cards';
import { Illustration } from '../common/Illustration';
import { LoadingState } from '../states/StateViews';
import type { PageSection } from '../../api/cms';
import { useLatestResults, useSiteConfig } from '../../hooks/useExamData';

type FeatureItem = {
  title: string;
  text: string;
  icon: string;
  route: string;
  tone: string;
  illustration?: string;
};

function HeroSection({ section }: { section: PageSection }) {
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfig();
  const config = section.configuration as {
    kicker?: string;
    primaryCta?: { label: string; route: string; icon?: string };
    secondaryCta?: { label: string; route: string };
    trustItems?: Array<{ icon: string; text: string }>;
    stats?: Array<{ label: string; value: string; tone: string; illustration?: string }>;
  };

  const platform = siteConfig?.platform;

  return (
    <section className="wfx-hero adaptive-page">
      <div className="wfx-hero-content">
        {config.kicker && <span className="section-kicker">{config.kicker}</span>}
        <h1>{platform?.tagline}</h1>
        <p className="wfx-hero-lead">{platform?.subtagline}</p>
        <div className="wfx-hero-actions">
          {config.primaryCta && (
            <button type="button" className="primary-action" onClick={() => navigate(config.primaryCta!.route)}>
              {config.primaryCta.label}
              {config.primaryCta.icon && (() => { const Icon = resolveIcon(config.primaryCta!.icon); return <Icon size={18} />; })()}
            </button>
          )}
          {config.secondaryCta && (
            <button type="button" className="secondary-action" onClick={() => navigate(config.secondaryCta!.route)}>
              {config.secondaryCta.label}
            </button>
          )}
        </div>
        {config.trustItems && (
          <div className="wfx-trust-bar">
            {config.trustItems.map((item) => {
              const Icon = resolveIcon(item.icon);
              return <span key={item.text}><Icon size={16} /> {item.text}</span>;
            })}
          </div>
        )}
      </div>
      {config.stats && config.stats.length > 0 && (
        <div className="wfx-hero-stats">
          {config.stats.map((stat) => (
            <StatCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              tone={stat.tone}
              illustration={stat.illustration}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function FeatureGridSection({ section }: { section: PageSection }) {
  const navigate = useNavigate();
  const config = section.configuration as {
    kicker?: string;
    illustration?: string;
    items?: FeatureItem[];
  };

  const items = config.items ?? [];

  return (
    <section className="adaptive-page wfx-feature-section">
      <div className="wfx-section-with-illustration">
        <SectionHeader
          kicker={config.kicker}
          title={section.title ?? ''}
          description={section.subtitle ?? undefined}
        />
        {config.illustration && (
          <Illustration context={config.illustration} size="md" className="wfx-section-illustration" />
        )}
      </div>
      <div className="wfx-feature-grid wfx-feature-carousel">
        {items.map((feature) => (
          <button
            key={feature.title}
            type="button"
            className={`feature-card wfx-feature-card tone-${feature.tone}`}
            onClick={() => navigate(feature.route)}
          >
            {feature.illustration && (
              <Illustration
                context={feature.illustration}
                size="sm"
                loading="eager"
                className="wfx-feature-card-illustration"
              />
            )}
            <strong>{feature.title}</strong>
            <p>{feature.text}</p>
            <span className="wfx-feature-link">Explore <ArrowRight size={14} /></span>
          </button>
        ))}
      </div>
    </section>
  );
}

function LatestResultsSection({ section }: { section: PageSection }) {
  const navigate = useNavigate();
  const config = section.configuration as {
    kicker?: string;
    limit?: number;
    viewAllRoute?: string;
    illustration?: string;
  };
  const limit = config.limit ?? 3;
  const { data, isLoading } = useLatestResults(limit);
  const results = data?.data ?? [];

  return (
    <section className="adaptive-page">
      <div className="wfx-latest-results-layout">
        <div className="wfx-latest-results-main">
          <SectionHeader
            kicker={config.kicker}
            title={section.title ?? ''}
            action={
              config.viewAllRoute ? (
                <button type="button" className="text-action" onClick={() => navigate(config.viewAllRoute!)}>
                  View all
                </button>
              ) : undefined
            }
          />
          <div className="wfx-result-grid">
            {isLoading ? (
              <LoadingState label="Loading latest results…" />
            ) : (
              results.map((result) => <ResultCard key={result.id} result={result} />)
            )}
          </div>
        </div>
        {config.illustration && (
          <Illustration
            context={config.illustration}
            size="md"
            className="wfx-latest-results-illustration"
          />
        )}
      </div>
    </section>
  );
}

function CtaBannerSection({ section }: { section: PageSection }) {
  const navigate = useNavigate();
  const config = section.configuration as {
    kicker?: string;
    illustration?: string;
    cta?: { label: string; route: string; icon?: string };
  };

  return (
    <section className="adaptive-page wfx-cta-banner glass-surface">
      <div className="wfx-cta-banner-content">
        {config.kicker && <span className="section-kicker">{config.kicker}</span>}
        <h2>{section.title}</h2>
        <p>{section.subtitle}</p>
        {config.cta && (
          <button type="button" className="primary-action" onClick={() => navigate(config.cta!.route)}>
            {config.cta.label}
            {config.cta.icon && (() => { const Icon = resolveIcon(config.cta!.icon); return <Icon size={18} />; })()}
          </button>
        )}
      </div>
      {config.illustration && (
        <Illustration context={config.illustration} size="md" className="wfx-cta-illustration" />
      )}
    </section>
  );
}

export function FooterSection() {
  const navigate = useNavigate();
  const { data: siteConfig } = useSiteConfig();
  const platform = siteConfig?.platform;
  const footer = siteConfig?.footer;

  if (!platform) return null;

  return (
    <footer className="wfx-footer adaptive-page">
      <div className="wfx-footer-cta glass-surface">
        <div className="wfx-footer-cta-text">
          <span className="section-kicker">Need help?</span>
          <strong>Questions about results, vault, or notifications?</strong>
          <p>Our help center covers everything from saving exams to attaching documents from your vault.</p>
          <button type="button" className="secondary-action compact" onClick={() => navigate('/settings')}>
            Open settings & help
          </button>
        </div>
        <Illustration context="support.help" size="sm" className="wfx-footer-cta-illustration" />
      </div>
      <div className="wfx-footer-grid">
        <div>
          <strong>{platform.name}</strong>
          <p>{footer?.description}</p>
        </div>
        {(footer?.columns ?? []).map((col) => (
          <div key={col.title}>
            <strong>{col.title}</strong>
            {col.links.map((link) => (
              <button key={link.label} type="button" className="text-action" onClick={() => navigate(link.route)}>
                {link.label}
              </button>
            ))}
          </div>
        ))}
      </div>
      <p className="wfx-footer-copy">{platform.copyright}</p>
    </footer>
  );
}

export function FaqSection({
  title,
  subtitle,
  items,
  kicker = 'Help',
  embedded = false,
}: {
  title: string;
  subtitle?: string | null;
  items: FaqItem[];
  kicker?: string;
  embedded?: boolean;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  if (items.length === 0) return null;

  return (
    <section
      className={`wfx-faq-section${embedded ? ' is-embedded' : ' adaptive-page'}`}
      aria-labelledby="wfx-faq-heading"
    >
      <div className="wfx-faq-layout">
        <div className="wfx-faq-intro">
          <header className="wfx-faq-header">
            {kicker && <span className="section-kicker">{kicker}</span>}
            <h2 id="wfx-faq-heading">{title}</h2>
            {subtitle && <p className="wfx-lead">{subtitle}</p>}
          </header>
          <Illustration context="faq.general" size="md" className="wfx-faq-illustration" />
        </div>
        <div className="wfx-faq-list" role="tablist" aria-label="FAQ items">
          {items.map((item, index) => {
            const isOpen = openIndex === index;
            const Icon = resolveIcon(item.icon, resolveIcon('BookOpen'));
            return (
              <div
                key={item.q}
                className={`wfx-faq-item glass-surface${isOpen ? ' is-open' : ''}`}
              >
                <button
                  type="button"
                  className="wfx-faq-trigger"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <span className="wfx-faq-trigger-main">
                    <span className="wfx-faq-trigger-icon" aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <span>{item.q}</span>
                  </span>
                  <ChevronDown size={18} className="wfx-faq-chevron" aria-hidden="true" />
                </button>
                {isOpen && (
                  <div className="wfx-faq-answer">
                    <FaqVisualAnswer item={item} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function HomePageSections({ sections }: { sections: PageSection[] }) {
  return (
    <>
      {sections.map((section) => {
        switch (section.sectionType) {
          case 'hero':
            return <HeroSection key={section.id} section={section} />;
          case 'feature_grid':
            return <FeatureGridSection key={section.id} section={section} />;
          case 'latest_results':
            return <LatestResultsSection key={section.id} section={section} />;
          case 'cta_banner':
            return <CtaBannerSection key={section.id} section={section} />;
          default:
            return null;
        }
      })}
      <FooterSection />
    </>
  );
}

export function PageHeaderSection({
  section,
  onActionClick,
  primaryCta,
  secondaryCta,
}: {
  section: PageSection;
  onActionClick?: () => void;
  primaryCta?: { label: string; onClick: () => void };
  secondaryCta?: { label: string; onClick: () => void };
}) {
  const navigate = useNavigate();
  const config = section.configuration as {
    kicker?: string;
    illustration?: string;
    action?: { label: string; icon?: string };
    primaryCta?: { label: string; route: string; icon?: string };
    secondaryCta?: { label: string; route: string };
  };
  const ActionIcon = config.action?.icon ? resolveIcon(config.action.icon) : null;

  const handlePrimary = primaryCta?.onClick ?? (config.primaryCta?.route
    ? () => {
        if (config.primaryCta!.route.startsWith('#')) {
          document.querySelector(config.primaryCta!.route)?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate(config.primaryCta!.route);
        }
      }
    : undefined);

  const handleSecondary = secondaryCta?.onClick ?? (config.secondaryCta?.route
    ? () => navigate(config.secondaryCta!.route)
    : undefined);

  return (
    <header className="wfx-page-header">
      <div className="wfx-page-header-text">
        <SectionHeader
          kicker={config.kicker}
          title={section.title ?? ''}
          description={section.subtitle ?? undefined}
          action={
            config.action && onActionClick ? (
              <button type="button" className="primary-action compact" onClick={onActionClick}>
                {ActionIcon && <ActionIcon size={16} aria-hidden="true" />}
                {config.action.label}
              </button>
            ) : undefined
          }
        />
        {(handlePrimary || handleSecondary) && (
          <div className="wfx-page-header-actions">
            {handlePrimary && (
              <button type="button" className="primary-action compact" onClick={handlePrimary}>
                {config.primaryCta?.icon && (() => { const Icon = resolveIcon(config.primaryCta!.icon); return <Icon size={16} />; })()}
                {primaryCta?.label ?? config.primaryCta?.label}
              </button>
            )}
            {handleSecondary && (
              <button type="button" className="secondary-action compact" onClick={handleSecondary}>
                {secondaryCta?.label ?? config.secondaryCta?.label}
              </button>
            )}
          </div>
        )}
      </div>
      {config.illustration && (
        <Illustration context={config.illustration} size="md" className="wfx-page-header-illustration" />
      )}
    </header>
  );
}
